import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminChrome from "../components/AdminChrome";
import { useRequireAdmin } from "./AdminLogin";
import { AudioPlayer, VideoPlayer } from "../components/MediaPlayer";
import TipTapEditor from "../components/TipTapEditor";
import DownloadPoster from "../components/DownloadPoster";

function MediaField({ label, value, onChange, accept, dataTestId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      alert(e.response?.data?.detail || "upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="xs-label">{label}</label>
      <div
        className={`xs-dropzone ${dragging ? "active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files?.[0]);
        }}
        data-testid={dataTestId}
      >
        {uploading ? (
          <div>uploading…</div>
        ) : value ? (
          <div>
            <div style={{ fontSize: "0.85em", wordBreak: "break-all", marginBottom: 6 }}>
              {value}
            </div>
            <div style={{ fontSize: "0.75em", opacity: 0.7 }}>click or drop to replace</div>
          </div>
        ) : (
          <div>
            <div>drop file here</div>
            <div style={{ fontSize: "0.75em", opacity: 0.7, marginTop: 4 }}>or click to browse</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
      {value && (
        <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="xs-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ fontSize: "0.8em", padding: "6px 10px" }}
          />
          <button className="xs-btn ghost" type="button" onClick={() => onChange("")}>clear</button>
        </div>
      )}
    </div>
  );
}

const EMPTY = {
  title: "", content: "", excerpt: "", cover_image_url: "",
  audio_url: "", video_url: "", tags: [], seo_title: "", seo_description: "",
};

export default function AdminEditPost() {
  useRequireAdmin();
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [post, setPost] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatingPoster, setGeneratingPoster] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api.get(`/posts/id/${id}`)
      .then((r) => setPost({ ...EMPTY, ...r.data }))
      .catch(() => { alert("failed to load"); nav("/admin"); })
      .finally(() => setLoading(false));
  }, [id, isNew, nav]);

  const update = (k, v) => setPost((p) => ({ ...p, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, "");
    if (!t) return;
    if (!post.tags.includes(t)) update("tags", [...post.tags, t]);
    setTagInput("");
  };

  const previewPost = async () => {
    try {
      const { data } = await api.post("/posts/preview", {
        title: post.title, content: post.content, excerpt: post.excerpt,
        cover_image_url: post.cover_image_url || null, audio_url: post.audio_url || null,
        video_url: post.video_url || null, tags: post.tags,
      });
      setPreview(data);
    } catch (e) {
      alert(e.response?.data?.detail || "preview failed");
    }
  };

  const checkOtsHealth = async () => {
    try {
      const { data } = await api.get("/admin/ots/health");
      setHealth(data);
    } catch (e) {
      alert(e.response?.data?.detail || "timestamp health check failed");
    }
  };

  const save = async () => {
    if (!post.title.trim() || !post.content.trim()) {
      alert("title and content are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        cover_image_url: post.cover_image_url || null,
        audio_url: post.audio_url || null,
        video_url: post.video_url || null,
        tags: post.tags,
        seo_title: post.seo_title || null,
        seo_description: post.seo_description || null,
      };
      if (isNew) {
        const { data } = await api.post("/posts", body);
        nav(`/admin/edit/${data.id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/posts/${id}`, body);
        setPost((current) => ({ ...current, ...data }));
      }
      return true;
    } catch (e) {
      alert(e.response?.data?.detail || "save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    const ok = await save();
    if (!ok && !isNew) return;
    if (!window.confirm("Publish this post and submit the SHA-256 hash to the OpenTimestamps Bitcoin calendar? This action is logged.")) return;
    const postId = isNew ? null : id;
    if (!postId) { alert("save first"); return; }
    setPublishing(true);
    try {
      const { data } = await api.post(`/posts/${postId}/publish`);
      setPost({ ...EMPTY, ...data });
      if (data.is_draft) {
        alert("timestamping failed or is still retrying; post was not published");
      } else {
        alert(`✓ published — sha256: ${data.blockchain_hash.slice(0, 16)}…\n⏳ timestamp status: ${data.opentimestamps_status}`);
      }
      // Poll OTS status in the background to update UI when it lands
      const poll = async (tries = 0) => {
        if (tries > 20) return;
        try {
          const { data: s } = await api.get(`/posts/${postId}/ots-status`);
          if (s.opentimestamps_status && s.opentimestamps_status !== "pending") {
            setPost((p) => ({ ...p, opentimestamps_status: s.opentimestamps_status }));
            if (s.verification === "calendar unreachable" || s.opentimestamps_status === "unreachable") {
              alert("timestamping failed; post was not published");
            }
            return;
          }
        } catch {}
        setTimeout(() => poll(tries + 1), 3000);
      };
      poll();
    } catch (e) {
      alert(e.response?.data?.detail || "publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const generatePoster = async () => {
    if (!post.id || post.is_draft) return;
    setGeneratingPoster(true);
    try {
      await api.post(`/posters/${post.id}/regenerate`);
      setPost((current) => ({ ...current, posters: { ...(current.posters || {}), status: "preparing" } }));
      const poll = async (tries = 0) => {
        if (tries > 20) return;
        try {
          const { data } = await api.get(`/posters/${post.id}/status`);
          setPost((current) => ({ ...current, posters: data }));
          if (data.status !== "preparing") return;
        } catch {}
        window.setTimeout(() => poll(tries + 1), 3000);
      };
      poll();
    } catch (e) {
      alert(e.response?.data?.detail || "poster generation failed");
    } finally { setGeneratingPoster(false); }
  };

  if (loading) return <div className="xs-page" style={{ textAlign: "center", padding: 80 }}>loading…</div>;

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-edit-page">
        <div className="xs-container" style={{ maxWidth: 900 }}>
          <AdminChrome />
          <h1 style={{ margin: "0 0 4px", color: "#ffde16" }}>
            {isNew ? "new post" : "edit post"}
          </h1>

          <label className="xs-label">title</label>
          <input
            className="xs-input"
            value={post.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="title"
            data-testid="post-title-input"
          />

          <label className="xs-label">excerpt</label>
          <input
            className="xs-input"
            value={post.excerpt || ""}
            onChange={(e) => update("excerpt", e.target.value)}
            placeholder="short summary (shown in cards & social previews)"
            data-testid="post-excerpt-input"
          />

          <label className="xs-label">content</label>
          <TipTapEditor
            value={post.content}
            onChange={(v) => update("content", v)}
            placeholder="tell the story. drop images, paste youtube links, use the toolbar or markdown shortcuts (## h2, **bold**, _italic_, > quote)."
          />
          <div style={{ marginTop: 10 }}>
            <button type="button" className="xs-btn ghost" onClick={previewPost}>preview hash</button>
            {preview && (
              <div style={{ marginTop: 8, fontSize: "0.82em", padding: 10, border: "1px solid rgba(255,222,22,.35)", borderRadius: 6 }}>
                <strong>preview sha256:</strong> <code style={{ wordBreak: "break-all" }}>{preview.hash}</code>
                <div style={{ marginTop: 4, opacity: 0.75 }}>{preview.notice}</div>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 4 }}>
            <MediaField
              label="cover image"
              value={post.cover_image_url}
              onChange={(v) => update("cover_image_url", v)}
              accept="image/*"
              dataTestId="cover-dropzone"
            />
            <MediaField
              label="audio (mp3/wav)"
              value={post.audio_url}
              onChange={(v) => update("audio_url", v)}
              accept="audio/*"
              dataTestId="audio-dropzone"
            />
          </div>

          <MediaField
            label="video (mp4/webm)"
            value={post.video_url}
            onChange={(v) => update("video_url", v)}
            accept="video/*"
            dataTestId="video-dropzone"
          />

          {/* preview */}
          {(post.cover_image_url || post.audio_url || post.video_url) && (
            <div style={{ marginTop: 16 }}>
              <label className="xs-label">media preview</label>
              {post.cover_image_url && (
                <img src={mediaUrl(post.cover_image_url)} alt="" className="xs-cover" style={{ maxHeight: 200 }} />
              )}
              {post.video_url && <VideoPlayer src={post.video_url} poster={post.cover_image_url} />}
              {post.audio_url && <AudioPlayer src={post.audio_url} />}
            </div>
          )}

          <label className="xs-label">tags</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            {post.tags.map((t) => (
              <span
                key={t}
                className="xs-tag"
                style={{ cursor: "pointer", color: "#ffde16" }}
                onClick={() => update("tags", post.tags.filter((x) => x !== t))}
                title="click to remove"
              >
                #{t} ×
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="xs-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="add tag and press enter"
              data-testid="tag-input"
            />
            <button className="xs-btn" type="button" onClick={addTag}>add</button>
          </div>

          <label className="xs-label">seo title (auto if blank)</label>
          <input
            className="xs-input"
            value={post.seo_title || ""}
            onChange={(e) => update("seo_title", e.target.value)}
          />
          <label className="xs-label">seo description (auto if blank)</label>
          <input
            className="xs-input"
            value={post.seo_description || ""}
            onChange={(e) => update("seo_description", e.target.value)}
          />

          {post.blockchain_hash && (
            <div style={{ marginTop: 22, padding: 14, border: "1px solid #ffde16", borderRadius: 8, fontSize: "0.85em" }}>
              <div><strong>status:</strong> {post.is_draft ? "DRAFT" : "PUBLISHED"}</div>
              <div style={{ wordBreak: "break-all" }}><strong>sha256:</strong> <code>{post.blockchain_hash}</code></div>
              <div><strong>opentimestamps:</strong> {post.opentimestamps_status || "—"}</div>
              {post.opentimestamps_verification_url && (
                <div><a href={post.opentimestamps_verification_url} target="_blank" rel="noreferrer">verify ↗</a></div>
              )}
              {!isNew && (
                <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className="xs-btn ghost" onClick={checkOtsHealth}>check timestamp health</button>
                  {health && <span style={{ alignSelf: "center", color: health.status === "pending" || health.status === "confirmed" ? "#6bff8a" : "#ff8a80" }}>{health.status}</span>}
                  {post.revisions?.length > 0 && <button type="button" className="xs-btn ghost" onClick={() => setHistoryOpen((open) => !open)}>{historyOpen ? "hide history" : "show history"}</button>}
                </div>
              )}
              {historyOpen && (
                <div style={{ marginTop: 10, padding: 12, border: "1px solid rgba(255,222,22,.35)", borderRadius: 8 }}>
                  {post.revisions.map((revision) => (
                    <div key={revision.revision} style={{ padding: "7px 0", borderBottom: "1px dashed rgba(255,222,22,.15)" }}>
                      <strong>revision {revision.revision}</strong> <span style={{ opacity: 0.7 }}>{revision.timestamp}</span>
                      <div style={{ wordBreak: "break-all", fontSize: "0.8em" }}>{revision.hash}</div>
                      <span>{revision.status} — {revision.change_note || "edited"}</span>
                      {revision.proof_url && <div><a href={mediaUrl(revision.proof_url)} download>download .ots</a></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="xs-btn" onClick={save} disabled={saving} data-testid="save-post-btn">
              {saving ? "saving…" : isNew ? "save draft" : "save"}
            </button>
            {!isNew && (
              <button
                className="xs-btn primary"
                onClick={publish}
                disabled={publishing}
                data-testid="publish-post-btn"
              >
                {publishing ? "publishing & timestamping…" : "publish & timestamp"}
              </button>
            )}
          </div>
          <DownloadPoster post={post} admin />
          {!isNew && !post.is_draft && (
            <button className="xs-btn ghost" type="button" onClick={generatePoster} disabled={generatingPoster} data-testid="generate-poster-btn">
              {generatingPoster ? "generating poster..." : "generate / regenerate poster"}
            </button>
          )}
        </div>
      </main>
      <Footer show />
    </div>
  );
}
